"use client";

import { useRouter } from "next/navigation";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useCallback, useEffect, useRef } from "react";
import { usePostHogReady } from "@/components/providers/posthog-provider";
import { useAnalytics } from "@/lib/analytics/hooks";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { PRODUCTS } from "@/lib/mayar/products";
import { QUARTER_HERO_FLAG } from "@/lib/pricing/quarter-hero";

function formatIdr(amount: number): string {
	return `Rp ${amount.toLocaleString("id-ID")}`;
}

export type UpgradeModalProps = {
	isOpen: boolean;
	onClose: () => void;
	t: Partial<Dictionary>;
	/** Optional hook fired the instant the user clicks a plan CTA (before the
	 * `/pricing` redirect) — lets a caller fire its own gate-specific
	 * telemetry (e.g. `gate_cta_clicked`) without this generic modal knowing
	 * which gate surface invoked it. */
	onCtaClick?: () => void;
};

export default function UpgradeModal({
	isOpen,
	onClose,
	t,
	onCtaClick,
}: UpgradeModalProps) {
	const { posthog } = useAnalytics();
	const isPostHogReady = usePostHogReady();
	const router = useRouter();
	const modalRef = useRef<HTMLDivElement>(null);
	const openedAtRef = useRef<number | null>(null);
	// Fails closed to false (no flag / not yet resolved) — the modal leads with
	// annual, the byte-identical today-path, until the flag resolves true.
	const quarterHeroEnabled = useFeatureFlagEnabled(QUARTER_HERO_FLAG) === true;

	const dismiss = useCallback(
		(method: "close_button" | "backdrop" | "escape_key") => {
			posthog.capture("upgrade_modal_dismissed", {
				dismiss_method: method,
				time_open_ms:
					openedAtRef.current != null ? Date.now() - openedAtRef.current : null,
			});
			onClose();
		},
		[posthog, onClose],
	);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") dismiss("escape_key");
		};
		if (isOpen) {
			openedAtRef.current = Date.now();
			document.addEventListener("keydown", handleEscape);
			document.body.style.overflow = "hidden";
		}
		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.style.overflow = "unset";
		};
	}, [isOpen, dismiss]);

	useEffect(() => {
		if (!isOpen || !isPostHogReady) return;
		posthog.capture("pricing_plan_viewed", {
			plan_name: quarterHeroEnabled ? "pro_quarterly" : "pro_annual",
			entry_surface: "upgrade_modal",
		});
	}, [isOpen, isPostHogReady, posthog, quarterHeroEnabled]);

	if (!isOpen) return null;

	const benefits = [
		t.advancedFilters,
		t.earlyAccessToNewJobs,
		t.premiumJobRanking,
	].filter(Boolean) as string[];

	const priceText = formatIdr(
		quarterHeroEnabled
			? PRODUCTS.pro_quarterly.amountIdr
			: PRODUCTS.pro_annual.amountIdr,
	);

	function navigateToPlan(
		productKey: "pro_quarterly" | "pro_monthly" | "pro_annual",
	) {
		posthog.capture("upgrade_modal_cta_clicked", {
			time_open_ms:
				openedAtRef.current != null ? Date.now() - openedAtRef.current : null,
			product_key: productKey,
		});
		onCtaClick?.();
		onClose();
		// Carry the intent so /pricing pre-selects the same plan and (for
		// logged-in users) one-click resumes checkout via SubscribeButton
		// autoStart.
		const intent =
			productKey === "pro_quarterly"
				? "quarterly"
				: productKey === "pro_monthly"
					? "monthly"
					: "annual";
		router.push(`/pricing?intent=${intent}&src=upgrade_modal`);
	}

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200"
			role="dialog"
			aria-modal="true"
			aria-labelledby="upgrade-modal-title"
		>
			<div
				ref={modalRef}
				className="bg-card rounded-2xl shadow-[0_5px_30px_rgba(0,0,0,0.22)] max-w-md w-full p-8 relative animate-in zoom-in-95 duration-200"
			>
				<button
					type="button"
					onClick={() => dismiss("close_button")}
					className="absolute right-4 top-4 text-muted-foreground hover:text-foreground p-1 transition-colors"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<title>Close</title>
						<path d="M18 6 6 18M6 6l12 12" />
					</svg>
				</button>

				<div className="text-center mb-6">
					<h2
						id="upgrade-modal-title"
						className="text-2xl font-bold tracking-tight"
					>
						{t.upgradeToPro}
					</h2>
					<p className="text-muted-foreground mt-2">
						{t.everythingInFree} {t.plus}:
					</p>
				</div>

				<div className="bg-muted/50 rounded-lg p-4 w-full mb-6 border border-border/50 text-center">
					<h3 className="font-semibold text-lg">
						{quarterHeroEnabled ? "Pro 3-Month" : "Pro Annual"}
					</h3>
					<div className="flex flex-col items-center mt-1">
						<span className="text-xl font-bold">
							{priceText}/{quarterHeroEnabled ? "3mo" : "year"}
						</span>
						<span className="text-xs text-muted-foreground mt-0.5 font-medium">
							One-time, non-recurring
						</span>
					</div>
				</div>

				<ul className="mt-4 text-sm text-left space-y-3 mb-8">
					{benefits.map((feature) => (
						<li key={feature} className="flex items-start gap-3">
							<span className="text-primary mt-0.5">✓</span>
							<span className="text-muted-foreground">{feature}</span>
						</li>
					))}
				</ul>

				<button
					type="button"
					onClick={() =>
						navigateToPlan(quarterHeroEnabled ? "pro_quarterly" : "pro_annual")
					}
					className="w-full bg-primary text-primary-foreground font-semibold py-3.5 px-6 rounded-full tracking-tight hover:opacity-90 transition-opacity flex items-center justify-center gap-2 group"
				>
					{quarterHeroEnabled ? "Get 3 months" : t.upgradeToPro}
					<svg
						role="img"
						aria-label="Arrow Right"
						xmlns="http://www.w3.org/2000/svg"
						width="18"
						height="18"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.5"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="group-hover:translate-x-1 transition-transform"
					>
						<title>Arrow Right</title>
						<path d="m9 18 6-6-6-6" />
					</svg>
				</button>

				{quarterHeroEnabled && (
					<button
						type="button"
						onClick={() => navigateToPlan("pro_monthly")}
						className="w-full mt-2.5 border border-border text-foreground font-semibold py-2.5 px-6 rounded-full tracking-tight hover:bg-muted transition-colors"
					>
						Or go monthly
					</button>
				)}

				<p className="text-xs text-center text-muted-foreground mt-4">
					Secure payment via Mayar.
				</p>
			</div>

			<button
				type="button"
				className="fixed inset-0 -z-10 appearance-none bg-transparent border-none cursor-default w-full h-full"
				onClick={() => dismiss("backdrop")}
				aria-label="Close modal"
				tabIndex={-1}
			/>
		</div>
	);
}
